import { useEffect, useState } from "react";

export interface Collab {
	name: string;
	url?: string;
}

export interface Media {
	key: string;
	kind: "image" | "video";
	url: string;
	/** Videos only: a still whose size and palette stand in for the video's */
	posterUrl?: string;
	alt?: string;
	/** ISO date the work is from; orders the homepage gallery */
	date?: string;
	/** Banner details shown while this piece is on screen */
	service?: string;
	credit?: string;
	backgroundColor?: string;
	width?: number;
	height?: number;
	palette?: { vibrant?: string; dominant?: string };
}

export interface Client {
	name: string;
	slug: string;
	url?: string;
	services: string[];
	collabs: Collab[];
	media: Media[];
	/** Shown in the homepage grid by default */
	featured: boolean;
}

export interface StudioData {
	services: string[];
	clients: Client[];
}

// Keep in sync with sanity/lib/slugify.ts, which sets the CMS slugs
export const slugify = (name: string) =>
	name
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[&+]/g, " and ")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const alphabetical = (a: string, b: string) =>
	a.localeCompare(b, "en", { sensitivity: "base", numeric: true });

type RawClient = {
	name?: string;
	slug?: string;
	url?: string;
	services?: (string | null)[];
	collabs?: (Collab | null)[];
	media?: (Omit<Media, "kind"> & { type: string } | null)[];
	featured?: boolean;
};

const normalizeClient = (c: RawClient): Client => ({
	name: c.name!,
	slug: c.slug || slugify(c.name!),
	url: c.url,
	services: (c.services ?? [])
		.filter((s): s is string => Boolean(s))
		.sort(alphabetical),
	collabs: (c.collabs ?? [])
		.filter((p): p is Collab => Boolean(p && p.name))
		.sort((a, b) => alphabetical(a.name, b.name)),
	media: (c.media ?? [])
		.filter((m): m is NonNullable<typeof m> => Boolean(m && m.url))
		.map(({ type, ...m }) => ({
			...m,
			kind: type === "clientVideo" ? "video" : "image",
		})),
	featured: Boolean(c.featured),
});

const fallbackClients: RawClient[] = [
	{
		name: "1906",
		url: "https://1906.shop/",
		services: [
			"Art Direction",
			"Creative Direction",
			"Marketing Collateral",
			"Packaging",
			"Production",
			"Visual Merchandising",
		],
	},
	{
		name: "ALCOVE",
		url: "https://www.reservealcove.com/",
		services: ["Branding", "Web Design", "Web Development"],
	},
	{
		name: "ALSOLIS",
		url: "https://alsolis.com/",
		services: ["Branding", "Web Design", "Web Development"],
	},
	{
		name: "Dune Suncare",
		url: "https://dunesuncare.com/",
		services: ["Marketing Collateral", "Production", "Special Projects"],
	},
	{
		name: "Eleasium",
		url: "https://eleasium.world/",
		services: ["Branding", "Web Design", "Web Development"],
	},
	{
		name: "Funner",
		url: "https://www.shopfunner.com",
		services: ["Special Projects", "Web Design"],
	},
	{
		name: "greige basics",
		url: "https://greigebasics.com/",
		services: ["Branding", "Web Design", "Web Development"],
	},
	{
		name: "Hudson Cannabis",
		url: "https://www.hudsoncannabis.co/",
		services: [
			"Art Direction",
			"Marketing Collateral",
			"Production",
			"Visual Merchandising",
			"Web Design",
			"Web Development",
		],
	},
	{
		name: "Mockly",
		url: "https://drinkmockly.com/",
		services: ["Marketing Collateral"],
	},
	{
		name: "Negative Underwear",
		url: "https://negativeunderwear.com/",
		services: ["Special Projects", "Web Design"],
	},
	{
		name: "Nili Lotan",
		url: "https://www.nililotan.com/",
		services: ["Web Design"],
	},
	{
		name: "Nori",
		url: "https://nori.co/",
		services: ["Packaging", "Visual Merchandising"],
	},
	{
		name: "Selima Optique",
		url: "https://selimaoptique.com/",
		services: ["Marketing Collateral"],
	},
	{
		name: "Soft Services",
		url: "https://softservices.com/",
		services: ["Special Projects", "Web Design", "Web Development"],
	},
	{
		name: "Stars + Honey",
		url: "https://starsandhoney.com/",
		services: ["Creative Direction", "Marketing Collateral", "Web Design"],
	},
	{
		name: "TINGE Beauty",
		url: "https://tingebeauty.com/",
		services: ["Marketing Collateral"],
	},
];

const fallbackData: StudioData = {
	services: [
		"Art Direction",
		"Branding",
		"Creative Direction",
		"Digital Strategy",
		"Marketing Collateral",
		"Packaging",
		"Production",
		"Project Management",
		"Special Projects",
		"Visual Merchandising",
		"Web Design",
		"Web Development",
	],
	clients: fallbackClients.map(normalizeClient),
};

const SANITY_QUERY_URL =
	"https://3ti0i8bf.api.sanity.io/v2023-07-04/data/query/production?query=" +
	encodeURIComponent(
		`{
			"services": *[_type == "service"].title,
			"clients": *[_type == "client"]{
				name,
				"slug": slug.current,
				url,
				"services": services[]->title,
				"collabs": collabs[]->{name, url},
				featured,
				"media": media[]{
					"key": _key,
					"type": _type,
					alt,
					"date": coalesce(date, asset->_createdAt),
					service,
					credit,
					backgroundColor,
					"url": asset->url,
					"posterUrl": poster.asset->url,
					...coalesce(asset->metadata, poster.asset->metadata){
						"width": dimensions.width,
						"height": dimensions.height,
						"palette": palette{
							"vibrant": vibrant.background,
							"dominant": dominant.background
						}
					}
				}
			}
		}`
	);

// One request per visit, shared by every page
let loaded: StudioData | null = null;
let request: Promise<StudioData | null> | null = null;

const loadStudioData = () => {
	request ??= fetch(SANITY_QUERY_URL)
		.then((res) => res.json())
		.then(({ result }) => {
			if (!result) return null;
			const data: StudioData = {
				services:
					Array.isArray(result.services) && result.services.length
						? [...result.services].sort(alphabetical)
						: fallbackData.services,
				clients:
					Array.isArray(result.clients) && result.clients.length
						? result.clients
								.filter((c: RawClient) => c && c.name)
								.map(normalizeClient)
								.sort((a: Client, b: Client) => alphabetical(a.name, b.name))
						: fallbackData.clients,
			};
			loaded = data;
			return data;
		})
		.catch(() => null);
	return request;
};

/** Studio content, plus whether the CMS has answered yet (fallback until then) */
export const useStudioData = () => {
	const [data, setData] = useState<StudioData>(loaded ?? fallbackData);
	const [settled, setSettled] = useState(loaded !== null);

	useEffect(() => {
		if (loaded) return;
		let cancelled = false;
		loadStudioData().then((result) => {
			if (cancelled) return;
			if (result) setData(result);
			setSettled(true);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	return { ...data, settled };
};

export const isVector = (url: string) => /\.svg$/i.test(url);

/**
 * Sanity image CDN URL at a given pixel width, in the best format the
 * browser supports. SVGs are served untouched, since resizing would turn
 * them into pixels.
 */
export const imageUrl = (url: string, width: number) =>
	isVector(url) ? url : `${url}?w=${Math.round(width)}&q=85&auto=format&fit=max`;
